import { Reference } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import ConfettiExplosion from 'react-confetti-explosion';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { SwiperSlide } from 'swiper/react';

import ActionIconButton from '@components/buttons/ActionIconButton';
import BackButton from '@components/buttons/BackButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import ArticleCard from '@components/cards/ArticleCard';
import ArchiveSegment from '@components/carePlan/ArchiveSegment';
import CarePlanActionItem from '@components/carePlan/CarePlanActionItem';
import ExpertService from '@components/carePlan/ExpertService';
import Tool from '@components/carePlan/Tool';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import EditModeBanner from '@components/EditModeBanner';
import ExpertAvatar from '@components/ExpertAvatar';
import EmployerBenefitCard from '@components/explore/EmployerBenefitCard';
import ToggleCheckbox from '@components/fields/ToggleCheckbox';
import CarePlanActionItemForm from '@components/forms/CarePlanActionItemForm';
import TaskCancelForm from '@components/forms/TaskCancelForm';
import TaskForm from '@components/forms/TaskForm';
import styles from '@components/Goal.module.css';
import CardSlides from '@components/layout/CardSlides';
import ColumnGrid from '@components/layout/ColumnGrid';
import Section from '@components/layout/Section';
import LegalDocumentTracker from '@components/LegalDocumentTracker';
import MedicationTracker from '@components/MedicationTracker';
import Modal, { ModalElement } from '@components/Modal';
import ProviderTracker from '@components/ProviderTracker';
import ScheduleConsultButton from '@components/ScheduleConsultButton';
import Icon from '@components/text/Icon';
import MiniCopy from '@components/text/MiniCopy';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import ButtonLink from '@components/typography/ButtonLink';
import Heading1 from '@components/typography/Heading1';
import Heading2 from '@components/typography/Heading2';
import useDestroyCarePlanActionItem from '@hooks/mutations/useDestroyCarePlanActionItem';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  CarePlanActionItemFragment,
  CarePlanActionItemToggleCompletedDocument,
  CarePlanActionItemToggleCompletedMutation,
  CarePlanActionItemToggleCompletedMutationVariables,
  ClientRoleRoleEnum,
  GetJourneyWithPlanQuery,
  GoalDestroyDocument,
  GoalDestroyMutation,
  GoalDestroyMutationVariables,
  GoalFragment,
  GoalMarkReadDocument,
  GoalMarkReadMutation,
  GoalMarkReadMutationVariables,
  GoalUpdateDocument,
  GoalUpdateMutation,
  GoalUpdateMutationVariables,
  TaskDestroyDocument,
  TaskDestroyMutation,
  TaskDestroyMutationVariables,
  TaskFragment
} from '@typing/Generated';
import {
  groupTools,
  medicationsBatched,
  primaryExpertRole,
  providersBatched,
  sortByPosition,
  sortByTitle
} from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  goal: GoalFragment;
  journey: GetJourneyWithPlanQuery['journey'];
};

type Task = GetJourneyWithPlanQuery['journey']['goals'][0]['tasks'][0];

const Goal = ({ goal, journey }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [read, setRead] = useState(false);

  const [markGoalRead] = useMutationWithValidation<GoalMarkReadMutation, GoalMarkReadMutationVariables>(
    GoalMarkReadDocument,
    'goal'
  );

  useEffect(() => {
    if (!read && myClient?.id === journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.owner)?.client.id) {
      markGoalRead({
        id: goal.id
      });
      setRead(true);
    }
  }, [goal.id, journey.clientRoles, journey.id, markGoalRead, myClient?.id, read]);

  const [actionItemBeingEdited, setActionItemBeingEdited] = useState<CarePlanActionItemFragment | undefined>(undefined);
  const [isExploding, setIsExploding] = useState(false);
  const [taskBeingEdited, setTaskBeingEdited] = useState<Task | null>();
  const [selectedBenefitId, setSelectedBenefitId] = useState<string | null>(null);
  const taskModalRef = useRef<ModalElement>(null);
  const actionItemModalRef = useRef<ModalElement>(null);

  const taskOpener = useOpener();
  const taskCancelOpener = useOpener();
  const taskDeleteOpener = useOpener();
  const actionItemOpener = useOpener();
  const actionItemDeleteOpener = useOpener();
  const goalDeleteOpener = useOpener();
  const markActionItemsCompleteOpener = useOpener();

  const history = useHistory();

  const destroyCarePlanActionItem = useDestroyCarePlanActionItem(journey);

  const handleActionItemError = () => {
    if (actionItemModalRef.current) {
      actionItemModalRef.current.scrollToTop();
    }
  };

  const handleActionItemCreate = () => {
    setActionItemBeingEdited(undefined);
    actionItemOpener.open();
  };

  const handleActionItemDelete = () => {
    if (actionItemBeingEdited) {
      destroyCarePlanActionItem({ id: actionItemBeingEdited.id }).then(() => {
        actionItemDeleteOpener.close();
        actionItemOpener.close();
        setActionItemBeingEdited(undefined);
      });
    }
  };

  const primaryExpert = primaryExpertRole(journey.expertRoles)?.expert;

  const batchedMedications = medicationsBatched(journey.medications);

  const batchedProviders = providersBatched(journey.providers);

  const onActionItemEdit = (actionItem: CarePlanActionItemFragment) => {
    setActionItemBeingEdited(actionItem);
    actionItemOpener.open();
  };

  const onTaskEdit = (task: TaskFragment) => {
    setTaskBeingEdited(task);
    taskOpener.open();
  };

  const [destroyTask] = useMutationWithValidation<TaskDestroyMutation, TaskDestroyMutationVariables>(
    TaskDestroyDocument,
    'task',
    {
      update: (store, { data }) => {
        if (data?.taskDestroy?.id) {
          store.modify({
            fields: {
              clientVisibleTasks(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => data.taskDestroy?.id !== readField('id', ref));
              }
            },
            id: store.identify({ ...journey })
          });

          const goals = journey.goals.filter(goal => goal.tasks.some(t => t.id === data.taskDestroy?.id));

          goals.map(goal => {
            store.modify({
              fields: {
                tasks(cachedRefs: readonly Reference[], { readField }) {
                  return cachedRefs.filter(ref => data.taskDestroy?.id !== readField('id', ref));
                }
              },
              id: store.identify({ ...goal })
            });
          });
        }
      }
    }
  );

  const handleTaskDelete = () => {
    if (taskBeingEdited) {
      destroyTask({ id: taskBeingEdited.id }).then(() => {
        taskDeleteOpener.close();
        taskOpener.close();
        setTaskBeingEdited(undefined);
      });
    }
  };

  const legalOpener = useOpener();
  const medicationTrackerOpener = useOpener();
  const providerOpener = useOpener();

  const tools = useMemo(() => {
    const groups = groupTools({
      carePlanActionItems: goal.carePlanActionItems,
      goals: [goal],
      journey,
      worksheets: goal.worksheets
    });

    return groups.new.concat(groups.inProgress).concat(groups.completed);
  }, [goal, journey]);

  const [goalToggleCompleted] = useMutationWithValidation<GoalUpdateMutation, GoalUpdateMutationVariables>(
    GoalUpdateDocument,
    'goal',
    {
      update: store => {
        store.modify({
          fields: {
            completed() {
              return !goal.completed;
            }
          },
          id: store.identify({ ...goal })
        });
      }
    }
  );

  const [carePlanActionItemToggleCompleted] = useMutationWithValidation<
    CarePlanActionItemToggleCompletedMutation,
    CarePlanActionItemToggleCompletedMutationVariables
  >(CarePlanActionItemToggleCompletedDocument, 'carePlanActionItem');

  const completeAllActionItems = () =>
    Promise.all(
      goal.carePlanActionItems
        .filter(a => !a.completed)
        .map(ai =>
          carePlanActionItemToggleCompleted({
            completed: true,
            id: ai.id
          })
        )
    );

  const [goalArchived] = useMutationWithValidation<GoalUpdateMutation, GoalUpdateMutationVariables>(
    GoalUpdateDocument,
    'goal',
    {
      update: store => {
        store.modify({
          fields: {
            archived() {
              return !goal.archived;
            }
          },
          id: store.identify({ ...goal })
        });
      }
    }
  );

  const [goalDestroy] = useMutationWithValidation<GoalDestroyMutation, GoalDestroyMutationVariables>(
    GoalDestroyDocument,
    'goal'
  );

  const [cpaiArchived, setCpaiArchived] = useState(false);
  const [taskArchived, setTaskArchived] = useState(false);

  useEffect(() => {
    if (cpaiArchived && !goal.carePlanActionItems.some(a => a.archived)) {
      setCpaiArchived(false);
    }
    if (taskArchived && !goal.tasks.some(t => t.archived)) {
      setTaskArchived(false);
    }
  }, [cpaiArchived, goal.carePlanActionItems, goal.tasks, taskArchived]);

  const carePlanActionItems = goal.carePlanActionItems.filter(a => a.archived === cpaiArchived);
  const tasks = goal.tasks.filter(t => t.archived === taskArchived);

  const updateComplete = (skipActionItemCompletedCheck: boolean) => {
    const completed = !goal.completed;
    if (completed && !skipActionItemCompletedCheck && goal.carePlanActionItems.some(a => !a.completed)) {
      markActionItemsCompleteOpener.open();
    } else {
      goalToggleCompleted({
        completed,
        id: goal.id
      }).then(() => {
        if (completed) {
          setIsExploding(true);
          setTimeout(() => {
            setIsExploding(false);
          }, 1000);
        }
      });
    }
  };

  const updateArchived = () => {
    goalArchived({
      archived: !goal.archived,
      id: goal.id
    });
  };

  const destroyGoal = () => {
    goalDestroy({
      id: goal.id
    }).then(() => {
      history.push(journeyRoute({ journeyId: journey.id, linkAfterJourney: '/care-plan', myClient }));
    });
  };

  const sanitizedDescription = useSanitizeHtml(goal.description, journey.id, goal);

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    'appEvent'
  );

  const generateHandleEmployerBenefitClick = (employerBenefitId: string) => () => {
    if (selectedBenefitId === employerBenefitId) {
      setSelectedBenefitId(null);
    } else {
      setSelectedBenefitId(employerBenefitId);
      createAppEvent({
        actionKind: AppEventActionKindEnum.employer_benefit_opened,
        journeyId: journey.id,
        resourceId: employerBenefitId
      });
    }
  };

  return (
    <div>
      {goal.hasDuplicateGoalInEditMode && (
        <div className="ion-margin-bottom">
          <EditModeBanner />
        </div>
      )}
      <div className={styles.navigation}>
        <BackButton routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: '/care-plan', myClient })} />

        <Icon icon="chevronRight" />
        <BodyCopyBold color="primary700">
          <FormattedMessage id="pages.carePlan.base.goals.goals" />
        </BodyCopyBold>
        <BodyCopyBold color="primary300">{goal.title}</BodyCopyBold>
      </div>

      <div className={styles.headerContainer}>
        <div className={styles.headerGrid}>
          <div className={styles.header}>
            <div>
              <div className={styles.handHeartContainer}>
                <Icon color="white" icon="handHoldingHeart" />
              </div>
            </div>

            <div>
              <div>
                <Heading1>{goal.title}</Heading1>
              </div>
              <div>
                <BodyCopy color="neutral500">{sanitizedDescription}</BodyCopy>
              </div>
              {!goal.hasDuplicateGoalInEditMode && (
                <div className={styles.actions}>
                  <div>
                    {isExploding && (
                      <ConfettiExplosion
                        colors={['#5f49d3', '#82d4e8', '#ff7673', '#FAC7F3', '#F7DBF4']}
                        duration={2500}
                        force={0.6}
                        particleCount={85}
                        width={1000}
                      />
                    )}
                    <ToggleCheckbox
                      checked={goal.completed}
                      labelKey={
                        goal.completed
                          ? 'pages.carePlan.base.goals.markIncomplete'
                          : 'pages.carePlan.base.goals.markComplete'
                      }
                      name="goalCompleted"
                      onClick={() => updateComplete(false)}
                      skipSpacing
                      tabIndex={0}
                    />
                  </div>

                  <ButtonLink onClick={updateArchived} stylized={false}>
                    <div className={styles.goalAction}>
                      <Icon color="primary" icon="boxArchive" />
                      <MiniCopy color="primary700">
                        <FormattedMessage
                          id={
                            goal.archived ? 'pages.carePlan.base.goals.unarchive' : 'pages.carePlan.base.goals.archive'
                          }
                        />
                      </MiniCopy>
                    </div>
                  </ButtonLink>
                  <ButtonLink onClick={goalDeleteOpener.open} stylized={false}>
                    <div className={styles.goalAction}>
                      <Icon color="primary" icon="trash" />
                      <MiniCopy color="primary700">
                        <FormattedMessage id="pages.carePlan.base.goals.delete" />
                      </MiniCopy>
                    </div>
                  </ButtonLink>
                </div>
              )}
            </div>
          </div>
          <div />
        </div>
      </div>
      <ColumnGrid columns={2} focusSide="left">
        <div>
          {goal.carePlanActionItems.length > 0 && (
            <Section
              color="success500"
              descriptionKey="pages.carePlan.sectionTitles.actionItems.description"
              icon="checkmark"
              titleKey="pages.carePlan.sectionTitles.actionItems.title"
            >
              {goal.carePlanActionItems.some(ai => ai.archived) && (
                <ArchiveSegment
                  count={goal.carePlanActionItems.length - carePlanActionItems.length}
                  setValue={setCpaiArchived}
                  value={cpaiArchived}
                />
              )}

              <div className={styles.services}>
                {sortByPosition(carePlanActionItems).map(actionItem => (
                  <CarePlanActionItem
                    actionItem={actionItem}
                    journey={journey}
                    key={actionItem.id}
                    onEdit={onActionItemEdit}
                  />
                ))}
              </div>

              <div className={styles.add}>
                <ActionIconButton
                  ariaLabelKey="accessibility.ariaLabels.add"
                  disabled={goal.hasDuplicateGoalInEditMode}
                  icon="add"
                  onClick={handleActionItemCreate}
                  size="medium"
                />
              </div>
            </Section>
          )}
          <Section
            color="primary500"
            descriptionKey="pages.carePlan.sectionTitles.expertServices.description"
            icon="logoIcon"
            titleKey="pages.carePlan.sectionTitles.expertServices.title"
          >
            {goal.tasks.length === 0 ? (
              <div>
                <div className={styles.supportCard}>
                  {journey.primaryExpert && <ExpertAvatar expert={journey.primaryExpert} size={90} />}
                  <div>
                    <Heading2 bold={false} color="shadesWhite">
                      <FormattedMessage id="pages.carePlan.base.expertServices.supportStarts" />
                    </Heading2>
                    <div className={styles.supportDescription}>
                      <BodyCopy color="shadesWhite">
                        <FormattedMessage id="pages.carePlan.base.expertServices.supportStartsDescription" />
                      </BodyCopy>
                    </div>
                    <div className={styles.buttons}>
                      <ScheduleConsultButton journeyId={journey.id} size="small" />
                      <PrimaryButton
                        icon="messages"
                        routerLink={journeyRoute({
                          journeyId: journey.id,
                          linkAfterJourney: '/messages',
                          myClient
                        })}
                        size="small"
                        textKey="pages.carePlan.base.expertServices.messageUpper"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {goal.tasks.some(t => t.archived) && (
                  <ArchiveSegment
                    count={goal.tasks.length - tasks.length}
                    setValue={setTaskArchived}
                    value={taskArchived}
                  />
                )}
                <div className={styles.services}>
                  {tasks.map(task => (
                    <ExpertService handleEdit={onTaskEdit} journey={journey} key={task.id} task={task} />
                  ))}
                </div>
              </>
            )}
          </Section>
          <Section
            color="secondary700"
            descriptionKey="pages.carePlan.sectionTitles.tools.description"
            icon="pencil"
            titleKey="pages.carePlan.sectionTitles.tools.title"
          >
            <div className={styles.tools}>
              {tools.map(tool => (
                <Tool
                  archived={goal.archived}
                  goal={goal}
                  journeyId={journey.id}
                  key={tool.type ?? tool.worksheet?.id}
                  legalOpener={legalOpener}
                  medicationTrackerOpener={medicationTrackerOpener}
                  providerOpener={providerOpener}
                  tool={tool}
                />
              ))}
            </div>
          </Section>
        </div>
        {(goal.articles.length > 0 || goal.employerBenefits.length > 0) && (
          <Section color="error400" icon="globe" titleKey="pages.carePlan.sectionTitles.learn.title">
            <div className={styles.articlesContainer + ' ' + styles.hideMobile}>
              {goal.employerBenefits.map(employerBenefit => (
                <div key={employerBenefit.id} onClick={generateHandleEmployerBenefitClick(employerBenefit.id)}>
                  <div className={styles.articleCardSpacing} key={employerBenefit.id}>
                    <EmployerBenefitCard
                      employerBenefit={employerBenefit}
                      expanded={selectedBenefitId === employerBenefit.id}
                      goal={goal}
                      journeyId={journey.id}
                      layout="horizontal"
                    />
                  </div>
                </div>
              ))}

              {sortByTitle(goal.articles).map(article => (
                <div className={styles.articleCardSpacing} key={article.id}>
                  <ArticleCard article={article} goal={goal} journeyId={journey.id} key={article.id} />
                </div>
              ))}
            </div>
            <div className={styles.showMobile}>
              <CardSlides itemCount={goal.articles.length}>
                {goal.articles.map(article => (
                  <SwiperSlide key={article.id}>
                    <ArticleCard article={article} goal={goal} journeyId={journey.id} key={article.id} />
                  </SwiperSlide>
                ))}
              </CardSlides>
            </div>
          </Section>
        )}
      </ColumnGrid>

      <Modal
        opener={actionItemOpener}
        ref={actionItemModalRef}
        title={intl.formatMessage({
          id: actionItemBeingEdited
            ? 'models.carePlanActionItem.editClientTask'
            : 'models.carePlanActionItem.addClientTask'
        })}
      >
        {({ modalDisplayed }) => (
          <CarePlanActionItemForm
            autofocus={modalDisplayed}
            carePlanActionItem={actionItemBeingEdited}
            goal={goal}
            journey={journey}
            onCancel={actionItemOpener.close}
            onError={handleActionItemError}
            onSuccess={actionItemOpener.close}
          />
        )}
      </Modal>
      <DeleteConfirmationAlert
        messageKey="pages.carePlanActionItem.reallyDelete"
        onDelete={handleActionItemDelete}
        opener={actionItemDeleteOpener}
      />
      <Modal
        opener={taskOpener}
        ref={taskModalRef}
        title={intl.formatMessage({
          id: taskBeingEdited ? 'models.tasks.editTask' : 'models.tasks.addTask'
        })}
      >
        {({ modalDisplayed }) => (
          <TaskForm
            autofocus={modalDisplayed}
            clientVisibleTaskForm
            journey={journey}
            onCancel={taskOpener.close}
            onSuccess={taskOpener.close}
            primaryExpertId={primaryExpert?.id}
            task={taskBeingEdited}
          />
        )}
      </Modal>
      <Modal opener={taskCancelOpener}>
        {() => {
          if (taskBeingEdited) {
            return (
              <TaskCancelForm
                onCancel={taskCancelOpener.close}
                onSuccess={taskCancelOpener.close}
                taskId={taskBeingEdited.id}
              />
            );
          }
          return null;
        }}
      </Modal>

      <DeleteConfirmationAlert
        cancelKey="pages.carePlan.base.goals.associatedTasks.no"
        deleteKey="pages.carePlan.base.goals.associatedTasks.yes"
        headerKey="pages.carePlan.base.goals.associatedTasks.title"
        onCancel={() => {
          updateComplete(true);
          markActionItemsCompleteOpener.close();
        }}
        onDelete={() => {
          completeAllActionItems().then(() => {
            updateComplete(true);
            markActionItemsCompleteOpener.close();
          });
        }}
        opener={markActionItemsCompleteOpener}
      />

      <DeleteConfirmationAlert
        messageKey="pages.carePlan.task.reallyDelete"
        onDelete={handleTaskDelete}
        opener={taskDeleteOpener}
      />
      <DeleteConfirmationAlert
        messageKey="pages.carePlan.base.goals.reallyDelete"
        onDelete={destroyGoal}
        opener={goalDeleteOpener}
      />
      <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalOpener} />
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={providerOpener}
        restartWizard
      />
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={medicationTrackerOpener}
        restartWizard
      />
    </div>
  );
};

export default Goal;
